import styled from 'styled-components/macro'
import { useEffect, useRef, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { FilterStore } from 'components/Filters/FilterStore'
import { useSearchWithSortReset } from 'hooks/useSearchWithSortReset'
import { PiMagnifyingGlass } from 'react-icons/pi'

export function TableSearch({
  searchPlaceholder,
  filterStore,
  className,
  tableKey,
  onFocus,
  onBlur,
}: {
  searchPlaceholder: string
  filterStore: FilterStore
  className?: string
  /** Optional: If provided, automatically clears sorting when search is applied */
  tableKey?: string
  onFocus?: () => void
  onBlur?: () => void
}) {
  const { search: searchFilter, setSearch } = filterStore()
  const search = searchFilter?.['search'] ?? { value: '', label: 'Search' }
  const searchValue = search.value
  const searchLabel = search.label

  // Ensures sorting is cleared when search is applied
  useSearchWithSortReset(filterStore, tableKey)

  const [internalSearch, setInternalSearch] = useState(search)
  const [debouncedSearch] = useDebounce(internalSearch, 500)
  const isUserSearchChangeRef = useRef(false)

  useEffect(() => {
    setInternalSearch((currentSearch) => {
      if (
        currentSearch.value === searchValue &&
        currentSearch.label === searchLabel
      ) {
        return currentSearch
      }

      isUserSearchChangeRef.current = false
      return { value: searchValue, label: searchLabel }
    })
  }, [searchLabel, searchValue])

  useEffect(() => {
    if (!isUserSearchChangeRef.current) return
    if (debouncedSearch.value === searchValue) {
      isUserSearchChangeRef.current = false
      return
    }

    setSearch('search', debouncedSearch.value, 'Search')
    isUserSearchChangeRef.current = false
  }, [debouncedSearch, searchValue, setSearch])

  function onChange(e: any) {
    isUserSearchChangeRef.current = true
    setInternalSearch({ value: e.target.value as string, label: 'Search' })
  }

  return (
    <div style={{ position: 'relative' }} className={className}>
      <IconContainer>
        <PiMagnifyingGlass />
      </IconContainer>
      <Input
        type="text"
        value={internalSearch.value}
        onChange={onChange}
        placeholder={searchPlaceholder}
        className="form-control"
        style={{ width: '100%' }}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    </div>
  )
}

export const IconContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  display: flex;
  align-items: center;
  background: transparent;
  padding-left: 10px;
  font-size: 18px;
`

export const Input = styled.input`
  display: flex;
  align-items: center;
  padding: 8px 14px;
  justify-content: space-between;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  gap: 10px;
  width: 320px;
  padding-left: 30px;
  height: 34px;
`
